import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { SUBJECT_COLORS, CHART_COLORS } from '../../../constants/colors'

interface UnitData {
  unitCode: string
  unitName: string
  subjectCode: string
  subjectName?: string
  totalQuestions: number
  avgQuestionsPerExam: number
  importanceScore: number
}

// Helper to get subject color from various code formats
function getSubjectColor(subjectCode?: string, subjectName?: string): string {
  const code = (subjectCode || subjectName || '').toUpperCase()

  if (code.includes('PHY')) {
    return SUBJECT_COLORS.NEET_UG_PHY
  }
  if (code.includes('CHEM')) {
    return SUBJECT_COLORS.NEET_UG_CHEM
  }
  if (code.includes('BIO') || code.includes('BOTANY') || code.includes('ZOOLOGY')) {
    return SUBJECT_COLORS.NEET_UG_BIO
  }

  return CHART_COLORS.primary // Default
}

interface ChartUnitData extends UnitData {
  name: string
}

interface HighYieldBarChartProps {
  data: UnitData[]
  onUnitClick?: (unitCode: string) => void
}

export default function HighYieldBarChart({ data, onUnitClick }: HighYieldBarChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        No high-yield unit data available
      </div>
    )
  }

  const chartData: ChartUnitData[] = data.slice(0, 10).map((unit) => ({
    ...unit,
    name: (unit.unitName || 'Unknown').length > 20
      ? (unit.unitName || 'Unknown').substring(0, 20) + '...'
      : (unit.unitName || 'Unknown'),
  }))

  const handleBarClick = (entry: ChartUnitData) => {
    if (onUnitClick) {
      onUnitClick(entry.unitCode)
    }
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 12 }} />
        <YAxis
          type="category"
          dataKey="name"
          width={150}
          tick={{ fontSize: 11 }}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null
            const unitData = payload[0].payload as ChartUnitData
            return (
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                <p className="font-medium text-gray-900">{unitData.unitName || 'Unknown Unit'}</p>
                <div className="mt-1 space-y-0.5 text-sm">
                  <p className="text-gray-600">
                    Total Questions: <span className="font-medium">{unitData.totalQuestions ?? 0}</span>
                  </p>
                  <p className="text-gray-600">
                    Avg/Exam: <span className="font-medium">{(unitData.avgQuestionsPerExam ?? 0).toFixed(1)}</span>
                  </p>
                  <p className="text-gray-600">
                    Importance: <span className="font-medium">{(unitData.importanceScore ?? 0).toFixed(1)}</span>
                  </p>
                </div>
              </div>
            )
          }}
        />
        <Bar
          dataKey="totalQuestions"
          radius={[0, 4, 4, 0]}
          cursor="pointer"
          onClick={(_, index) => handleBarClick(chartData[index])}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={getSubjectColor(entry.subjectCode, entry.subjectName)}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
